import { FC, useContext, useMemo } from "react";
import { useRecoilValue } from "recoil";

import { VirtualizedGrid } from "@ryde/controls";
import {
  DateYMDString,
  generateDatesArrayYMDString,
  IRidesCalendarEventDto,
  ScrollAreaEnum,
  topCalendarDatesAtomFamily,
  useTimezone,
} from "@ryde/shared";

import {
  buildRidesCalendarEventMap,
  getRidesCalendarVisibleHourIndices,
} from "../rides-calendar.helpers";
import { ridesCalendarRowsFilterAtom } from "./rides-calendar.atoms";
import { RidesCalendarCellRenderStrategy } from "./rides-calendar.cell-render-strategy";
import {
  RIDES_CALENDAR_DAY_COLUMN_WIDTH_PX,
  RIDES_CALENDAR_ENTITY,
  RIDES_CALENDAR_HEADER_ROW_HEIGHT,
  RIDES_CALENDAR_HOUR_COLUMN_WIDTH_PX,
  RIDES_CALENDAR_HOUR_ROW_COUNT,
  RIDES_CALENDAR_HOUR_ROW_HEIGHT_PX,
  RIDES_CALENDAR_LAST_ROW_SPACER_PX,
  RIDES_CALENDAR_SCROLLBAR_GUTTER_PX,
} from "./rides-calendar.constants";
import { RidesCalendarContext } from "./rides-calendar.context";
import { IRidesCalendarGridData } from "./rides-calendar.models";

interface IRidesCalendarWrapperProps {
  events: IRidesCalendarEventDto[];
  onOpenRide: (rideId: number) => void;
  onAddRide: (dateKey: DateYMDString, hour: number) => void;
}

export const RidesCalendarWrapper: FC<IRidesCalendarWrapperProps> = ({
  events,
  onOpenRide,
  onAddRide,
}) => {
  const calendarDates = useRecoilValue(
    topCalendarDatesAtomFamily(RIDES_CALENDAR_ENTITY),
  );
  const { timezone } = useTimezone();
  const rowsFilter = useRecoilValue(ridesCalendarRowsFilterAtom);
  const { ridesCalendarHeight, ridesCalendarWidth } =
    useContext(RidesCalendarContext);

  const dateKeys = useMemo(() => {
    if (!calendarDates?.date1 || !calendarDates?.date2) {
      return [];
    }
    return (
      generateDatesArrayYMDString(
        calendarDates.date1,
        calendarDates.date2,
        timezone,
      ) ?? []
    );
  }, [calendarDates, timezone]);

  const eventMap = useMemo(
    () => buildRidesCalendarEventMap({ events, dateKeys, timezone }),
    [events, dateKeys, timezone],
  );

  const visibleHourIndices = useMemo(
    () =>
      getRidesCalendarVisibleHourIndices({
        eventMap,
        filter: rowsFilter,
        hourRowCount: RIDES_CALENDAR_HOUR_ROW_COUNT,
      }),
    [eventMap, rowsFilter],
  );

  const gridData: IRidesCalendarGridData = useMemo(
    () => ({ dateKeys, eventMap, visibleHourIndices, onOpenRide, onAddRide }),
    [dateKeys, eventMap, visibleHourIndices, onOpenRide, onAddRide],
  );

  if (!dateKeys.length || !visibleHourIndices.length) {
    return null;
  }

  const totalColumnsWidth =
    RIDES_CALENDAR_HOUR_COLUMN_WIDTH_PX +
    dateKeys.length * RIDES_CALENDAR_DAY_COLUMN_WIDTH_PX;
  const hasHorizontalScrollbar = totalColumnsWidth > ridesCalendarWidth;
  const horizontalScrollbarGutter = hasHorizontalScrollbar
    ? RIDES_CALENDAR_SCROLLBAR_GUTTER_PX
    : 0;
  const adjustedHeight = Math.max(
    0,
    ridesCalendarHeight - horizontalScrollbarGutter,
  );

  const headerRowIndex = 0;
  const spacerRowIndex = visibleHourIndices.length + 1;
  const totalRowCount = spacerRowIndex + 1;

  return (
    <VirtualizedGrid
      scrollArea={ScrollAreaEnum.RidesCalendar}
      height={adjustedHeight}
      width={ridesCalendarWidth}
      frozenRowCount={1}
      frozenColumnCount={1}
      columnCount={dateKeys.length + 1}
      rowCount={totalRowCount}
      columnWidth={columnIndex =>
        columnIndex === 0
          ? RIDES_CALENDAR_HOUR_COLUMN_WIDTH_PX
          : RIDES_CALENDAR_DAY_COLUMN_WIDTH_PX
      }
      rowHeight={index => {
        if (index === headerRowIndex) {
          return RIDES_CALENDAR_HEADER_ROW_HEIGHT;
        }
        if (index === spacerRowIndex) {
          return RIDES_CALENDAR_LAST_ROW_SPACER_PX;
        }
        return RIDES_CALENDAR_HOUR_ROW_HEIGHT_PX;
      }}
      gridData={gridData}
      CellRenderer={RidesCalendarCellRenderStrategy}
    />
  );
};
